/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import { Box, Container, Divider, Snackbar, useMediaQuery } from '@material-ui/core';
import cn from 'classnames';
import { Redirect, Route, Switch, useRouteMatch, useLocation, useHistory } from 'react-router-dom';
import IdentificationConfirmationOptionSelection from './IdentificationConfirmationOptionSelection';
import IdentificationConfirmationPhoneCode from './IdentificationConfirmationPhoneCode';
import IdentificationConfirmationCard from './IdentificationConfirmationCard';
import IdentificationConfirmationEmailAddress from './IdentificationConfirmationEmailAddress';
import { BankType, IdentityConfirmationOptionType, useConfirmIdentityAdminMutation, useGetIdentityConfirmationOptionsQuery } from '../../generated/graphql';
import { TranquiTheme } from '../App/theme';
import DebtNegPreApprovedNoAuth from '../DebtsNegRoot/DebtNegPreApprovedNoAuth';
import Btn from '../../components/Btn/Btn';
import { BackBtnIcon } from '../../assets/icons/BackBtnIcon';
import RenderText from '../../dictionaries/RenderText';
import Footer from '../../components/Footer/Footer';
import { useStylesIdConfirmation } from './IdentificationConfirmationStyles';
import { IdentificactionConfirmationInitialPage } from './IdentificationConfirmationInitialPage';
import IdentificationConfirmationNoInformation from './IdentificationConfirmationNoInformation';
import { useGetProfileAndDebtsReport } from '../../utils/useGetProfileAndDebtsReport';
import PrivateRoute from '../../components/PrivateRoute/PrivateRoute';
import { useLogout } from '../../utils/useLogout';
import { useBankContext, useBrandContext } from '../../dictionaries/context/BankContext';
import { getBankImage } from '../../utils/getBankImage';
import { useBrandingContext } from '../../branding';
import { session } from '../../utils/auth';
import { useLocalAdminInfo } from '../../utils/useLocalAdminInfo';
import { useIdentityConfirmation } from './utils/useIdentityConfirmation';
import { initialRoute } from '../../utils/initialRoute';
import { EXCLUDED_BANK_LOGOS } from './utils/excluded_bank_logos';
import useDictionary from '../../dictionaries/context/useDictionary';

const SNACKBAR_DURATION = 8000; // 8 segundos

interface IdentificationConfirmationProps {}

const IdentificationConfirmation: React.FC<IdentificationConfirmationProps> = () => {
  const { logo: Logo, illustrations: { loginMain } } = useBrandingContext();
  const classes = useStylesIdConfirmation();

  const {
    hasToken, outDebtsReportData, profileData,
  } = useGetProfileAndDebtsReport();

  const history = useHistory();
  const location = useLocation();
  const { path, url } = useRouteMatch();
  const logout = useLogout();

  // If this has a value, we should redirect to such debt
  const outDebtId = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('outDebtId');
  }, [ location ]);

  const [ { token: adminToken } ] = useLocalAdminInfo();
  const [ brand ] = useBrandContext();
  const [ bank ] = useBankContext();

  const welcomePath = `${path}/inicio`;
  const documentPath = `${path}/ingresar`;

  const welcomeRedirectUrl = `${welcomePath}?bank=${bank}`;
  const documentRedirectUrl = `${documentPath}?bank=${bank}`;

  const noInfoPath = `${path}/sin-informacion`;
  const optionsPath = `${path}/opciones`;
  const productNumberPath = `${path}/verificar-tarjeta`;
  const emailPath = `${path}/verificar-email/:email`;
  const registeredPhonePath = `${path}/verificar-registrado/:digits(\\d{4})`;
  const debtPath = outDebtId ? `/portal/deuda/${outDebtId}?outDebtId=undefined` : '/portal/deudas';
  const redirectUrl = outDebtsReportData?.outDebtsReport.hasSome
    ? debtPath : noInfoPath;

  const { gT } = useDictionary();
  const {
    data: identityConfirmationOptionsData,
    loading: identityConfirmationOptionsLoading, refetch: refetchConfirmationOptions,
  } = useGetIdentityConfirmationOptionsQuery({
    skip: !hasToken || !bank || !brand,
    variables: { bank: bank!, brand: brand! },
  });

  const [ confirmAdmin, { loading: confirmAdminLoading, called: confirmAdminCalled } ] = useIdentityConfirmation(
    useConfirmIdentityAdminMutation,
    'confirmIdentityAdmin',
    url,
  );

  // Estado consolidado para manejar todos los errores de validación
  const [ exhaustedMethod, setExhaustedMethod ] = React.useState<string | null>(null);
  const document = profileData?.profile?.document || undefined;

  // Mensajes de error por método
  const exhaustedMessages: Record<string, string> = {
    card: gT('card_no_tries'),
    email: gT('email_no_tries'),
    phone: gT('phone_no_tries'),
  };

  React.useEffect(() => {
    if (adminToken && !confirmAdminLoading && !confirmAdminCalled && bank && !session.identityValidated) {
      confirmAdmin({
        variables: {
          brand: brand!,
          bank: bank!,
        },
      }).catch(() => {});
    }
  }, [ adminToken, confirmAdminLoading, confirmAdminCalled, brand, bank, confirmAdmin ]);

  const isWeb = useMediaQuery((theme: TranquiTheme) => theme.breakpoints.up('md'));

  // wait for options load if there is a bank
  if (bank && identityConfirmationOptionsLoading) return null;

  const identityOptions = identityConfirmationOptionsData?.identityConfirmationOptions || [];

  // Filtrar opciones que NO tienen intentos agotados
  const availableOptions = identityOptions
    .filter((opt) => !opt.attemptsExhausted);
  const hasIdentityOptions = identityConfirmationOptionsLoading || identityOptions.length > 0;

  const cardOption = identityOptions
    .find((opt) => opt.type === IdentityConfirmationOptionType.CcNumber);

  const emailOption = identityOptions
    .find((opt) => opt.type === IdentityConfirmationOptionType.Email);

  const phoneOption = identityOptions
    .find((opt) => opt.type === IdentityConfirmationOptionType.Cellphone);

  const checkPath = (inputPath: string) => {
    const clean = inputPath.replace(path, '').split('/').filter((v) => v)[0];
    return !!location.pathname.match(new RegExp(clean));
  };

  const handleBack = () => {
    if ([ emailPath, registeredPhonePath, productNumberPath ].some(checkPath)) {
      return history.push(path); // back to options
    }
    if ([ optionsPath, noInfoPath ].some(checkPath)) {
      history.push(`${location.pathname}?bank=${bank}`);
      return logout();
    }
    if (checkPath(documentPath)) {
      return history.push(welcomePath);
    }
    return undefined;
  };

  const bankImage = bank && getBankImage(bank);
  const inWelcomePage = checkPath(welcomePath);

  const nextRoute = initialRoute.get();
  if (session.identityValidated && nextRoute) {
    return <Redirect to={nextRoute} />;
  }

  // confirmed identity and phone or has no identity options and confirmed phone
  if (session.identityValidated && !checkPath(noInfoPath)) {
    return <Redirect to={redirectUrl} />; // then redirect
  }

  if (hasToken && [ welcomePath, documentPath ].includes(location.pathname) && hasIdentityOptions) {
    return <Redirect to={optionsPath} />;
  }

  const showBankImage = bankImage && !EXCLUDED_BANK_LOGOS[bank as BankType];

  return (
    <Box className={classes.root}>
      <Box className={classes.loginWrapper}>
        {isWeb && (
        <Box className={classes.logosWrapper}>
          <Logo />
          {showBankImage && (
            <>
              <Divider orientation="vertical" className="greenDivider" />
              <img style={{ height: 25 }} src={bankImage} alt="bank" />
            </>
          )}
        </Box>
        )}
        {!inWelcomePage && (
          <Btn variant="text" onClick={handleBack} className={classes.backBtn}>
            <BackBtnIcon className="btnIcon" />
            <RenderText textKey="validation_confirmation_back_button" />
          </Btn>
        )}

        {/* CONTENT */}
        <Container className={cn(classes.box, 'pt-3')}>
          <Switch>
            {/* PRIVATE ROUTES */}
            <PrivateRoute
              path={productNumberPath}
              to={welcomeRedirectUrl}
              render={() => {
                if (!cardOption) return <Redirect to={url} />;
                return (
                  <IdentificationConfirmationCard
                    baseClasses={classes}
                    parentUrl={url}
                    option={cardOption}
                    refetchConfirmationOptions={refetchConfirmationOptions}
                    setShowSnackbarCardError={() => setExhaustedMethod('card')}
                  />
                );
              }}
            />
            <PrivateRoute
              path={emailPath}
              to={welcomeRedirectUrl}
              render={() => {
                if (!emailOption) return <Redirect to={url} />;
                return (
                  <IdentificationConfirmationEmailAddress
                    baseClasses={classes}
                    parentUrl={url}
                    option={emailOption}
                    refetchConfirmationOptions={refetchConfirmationOptions}
                    setShowSnackbarEmailError={() => setExhaustedMethod('email')}
                  />
                );
              }}
            />
            <PrivateRoute
              path={registeredPhonePath}
              to={welcomeRedirectUrl}
              render={() => {
                if (!phoneOption) return <Redirect to={url} />;
                return (
                  <IdentificationConfirmationPhoneCode
                    redirectUrl={redirectUrl}
                    baseClasses={classes}
                    id={document}
                    parentUrl={url}
                    profile={profileData?.profile as any || undefined}
                    option={phoneOption}
                    refetchConfirmationOptions={refetchConfirmationOptions}
                    setShowSnackbarPhoneError={() => setExhaustedMethod('phone')}
                  />
                );
              }}
            />
            <PrivateRoute
              path={optionsPath}
              to={documentRedirectUrl}
              render={() => (
                <>
                  {!hasIdentityOptions && <Redirect to={noInfoPath} />}
                  <IdentificationConfirmationOptionSelection
                    baseClasses={classes}
                    id={document}
                    parentUrl={url}
                    identityOptions={availableOptions}
                    bank={bank as BankType}
                  />
                </>
              )}
            />
            <PrivateRoute
              path={noInfoPath}
              to={documentRedirectUrl}
              render={() => (
                <IdentificationConfirmationNoInformation
                  id={document}
                  baseClasses={classes}
                  hasIdentityOptions={hasIdentityOptions}
                />
              )}
            />

            {/* PUBLIC ROUTES */}
            <Route
              path={documentPath}
              render={() => (
                <DebtNegPreApprovedNoAuth
                  baseClasses={classes}
                />
              )}
            />
            <Route
              path={welcomePath}
              render={() => (
                <IdentificactionConfirmationInitialPage
                  parentUrl={url}
                  baseClasses={classes}
                />
              )}
            />
            <Redirect to={welcomeRedirectUrl} />
          </Switch>

          {/* Snackbar consolidado para todos los métodos de validación */}
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            autoHideDuration={SNACKBAR_DURATION}
            message={exhaustedMethod ? exhaustedMessages[exhaustedMethod] : ''}
            open={!!exhaustedMethod}
            onClose={() => setExhaustedMethod(null)}
          />
        </Container>

      </Box>
      <Box className={classes.welcomeWrapper}>
        {isWeb && (
          <Box className="welcomeImgWrapper">
            <img src={loginMain} alt="welcomeImage" />
          </Box>
        )}
        <Footer isIdValidation />
      </Box>
    </Box>
  );
};

export default IdentificationConfirmation;
